import React, { useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { colors } from '../theme';
import type { DevelopmentUnitRow } from './DevelopmentRentalAssumptions';
import { formatPSF } from '../utils/formatters';
import ReadOnlyCardList from './ReadOnlyCardList';

interface Props {
  rows: DevelopmentUnitRow[];
}

export const DevelopmentRentalAssumptionsReadOnly: React.FC<Props> = ({ rows }) => {
  const computed = useMemo(() => {
    const withDerived = (rows || []).map((r) => {
      const avgSf = Number(r.avg_sf || 0);
      const units = Number(r.units || 0);
      const avgRentMonthly = Number(r.avg_rent || 0);
      const totalSf = Math.max(0, Math.round(avgSf * units));
      const monthlyRent = Math.max(0, avgRentMonthly * units);
      const annualRent = monthlyRent * 12;
      const rentPsf = avgSf > 0 ? avgRentMonthly / avgSf : 0;
      return { ...r, totalSf, rentPsf, monthlyRent, annualRent };
    });
    const totals = withDerived.reduce(
      (acc, r: any) => {
        acc.units += Number(r.units || 0);
        acc.totalSf += Number(r.totalSf || 0);
        acc.monthlyRent += Number(r.monthlyRent || 0);
        acc.annualRent += Number(r.annualRent || 0);
        return acc;
      },
      { units: 0, totalSf: 0, monthlyRent: 0, annualRent: 0 }
    );
    const weightedRentPsf = totals.totalSf > 0 ? totals.monthlyRent / totals.totalSf : 0;
    const avgRent = totals.units > 0 ? Math.round(totals.monthlyRent / totals.units) : 0;
    return { withDerived, totals, weightedRentPsf, avgRent };
  }, [rows]);

  const columns: GridColDef[] = [
    {
      field: 'unit_type',
      headerName: 'Unit Type',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <span style={{ color: '#1f2937', fontSize: 14.5, fontFamily: 'inherit', fontWeight: 400 }}>
          {params.value || ''}
        </span>
      ),
    },
    {
      field: 'avg_sf',
      headerName: 'Avg. SF',
      type: 'number',
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <span className="u-muted">{Number(params.value ?? 0).toLocaleString()}</span>
      ),
    },
    {
      field: 'units',
      headerName: 'Units',
      type: 'number',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <span className="u-muted">{Number(params.value ?? 0).toLocaleString()}</span>
      ),
    },
    {
      field: 'total_sf',
      headerName: 'Total SF',
      type: 'number',
      flex: 0.9,
      minWidth: 120,
      valueGetter: (p: any) => ((p && p.row && (p.row as any).totalSf) ?? 0),
      renderCell: (params) => (
        <span className="u-muted">{Number((params.row as any).totalSf ?? 0).toLocaleString()}</span>
      ),
    },
    {
      field: 'rent_psf',
      headerName: 'Rent PSF/Mo',
      type: 'number',
      flex: 0.8,
      minWidth: 110,
      valueGetter: (p: any) => ((p && p.row && (p.row as any).rentPsf) ?? 0),
      renderCell: (params) => (
        <span className="u-muted">
          {formatPSF(Number((params.row as any).rentPsf ?? 0))}
        </span>
      ),
    },
    {
      field: 'avg_rent',
      headerName: 'Avg. Rent',
      type: 'number',
      flex: 0.9,
      minWidth: 130,
      renderCell: (params) => (
        <span className="u-muted">
          ${Number(params.value ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      field: 'monthly_rent',
      headerName: 'Monthly Rent',
      type: 'number',
      flex: 1.0,
      minWidth: 140,
      valueGetter: (p: any) => ((p && p.row && (p.row as any).monthlyRent) ?? 0),
      renderCell: (params) => (
        <span className="u-muted">
          ${Number((params.row as any).monthlyRent ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      ),
    },
    {
      field: 'annual_rent',
      headerName: 'Annual Rent',
      type: 'number',
      flex: 1.0,
      minWidth: 140,
      valueGetter: (p: any) => ((p && p.row && (p.row as any).annualRent) ?? 0),
      renderCell: (params) => (
        <span className="u-muted">
          ${Number((params.row as any).annualRent ?? 0).toLocaleString()}
        </span>
      ),
    },
  ];

  // Match DataGrid column flex: Unit Type, Avg. SF, Units, Total SF, Rent PSF/Mo, Avg. Rent, Monthly Rent, Annual Rent
  const footerGridTemplateColumns = '1.2fr 0.8fr 0.7fr 0.9fr 0.8fr 0.9fr 1fr 1fr';

  const cellPadding = '0 10px';
  const Footer = () => {
    return (
      <div
        style={{
          padding: '8px 0',
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #e0e0e0',
          display: 'grid',
          gridTemplateColumns: footerGridTemplateColumns,
          alignItems: 'center',
          fontSize: 14.5,
          minWidth: 0,
        }}
      >
        <div style={{ fontWeight: 600, padding: cellPadding }}>Total</div>
        <div style={{ padding: cellPadding }} />
        <div style={{ textAlign: 'right', padding: cellPadding }}>{computed.totals.units.toLocaleString()}</div>
        <div style={{ textAlign: 'right', padding: cellPadding }}>{computed.totals.totalSf.toLocaleString()}</div>
        <div style={{ textAlign: 'right', padding: cellPadding }}>{formatPSF(computed.weightedRentPsf)}</div>
        <div style={{ textAlign: 'right', padding: cellPadding }}>${computed.avgRent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        <div style={{ textAlign: 'right', padding: cellPadding }}>${computed.totals.monthlyRent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        <div style={{ textAlign: 'right', padding: cellPadding }}>${computed.totals.annualRent.toLocaleString()}</div>
      </div>
    );
  };

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  if (isMobile) {
    return (
      <Box>
        <ReadOnlyCardList columns={columns} rows={computed.withDerived} titleField="unit_type" getRowId={(r) => r.id} />
        <Box sx={{ textAlign: 'right', fontSize: 13, color: '#1f2937', mt: 1.5 }}>
          <div><span style={{ fontWeight: 600 }}>Units:</span> {computed.totals.units.toLocaleString()} · <span style={{ fontWeight: 600 }}>Total SF:</span> {computed.totals.totalSf.toLocaleString()}</div>
          <div><span style={{ fontWeight: 600 }}>Rent PSF/Mo:</span> {formatPSF(computed.weightedRentPsf)} · <span style={{ fontWeight: 600 }}>Avg. Rent:</span> ${computed.avgRent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div><span style={{ fontWeight: 600 }}>Monthly:</span> ${computed.totals.monthlyRent.toLocaleString(undefined, { maximumFractionDigits: 0 })} · <span style={{ fontWeight: 600 }}>Annual:</span> ${computed.totals.annualRent.toLocaleString()}</div>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <DataGrid
        rows={computed.withDerived}
        columns={columns.map((c) => ({ ...c, sortable: false }))}
        autoHeight
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableColumnSorting
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        slots={{ footer: Footer as any }}
        density="compact"
        columnHeaderHeight={52}
        rowHeight={52}
        sx={{
          border: `2px solid ${colors.navy}`,
          borderRadius: 1,
          background: '#fff',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: `${colors.navy} !important`,
            backgroundImage: 'none !important',
            color: '#fff',
            padding: 0,
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: `${colors.navy} !important`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: '#fff',
            fontWeight: 600,
            fontSize: 14.5,
          },
          '& .MuiDataGrid-columnSeparator': { color: 'rgba(255,255,255,0.35)' },
          // Scoped to the navy header: unscoped, this also whitened the
        // pagination arrows in the white footer, leaving them invisible on any
        // table past 100 rows.
        '& .MuiDataGrid-columnHeaders .MuiSvgIcon-root': { color: '#fff' },
        '& .MuiDataGrid-footerContainer .MuiIconButton-root': { color: colors.blue },
        '& .MuiDataGrid-footerContainer .MuiIconButton-root.Mui-disabled': {
          color: colors.grey[400],
        },
          '& .MuiDataGrid-withBorderColor': { borderColor: colors.navy },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(15,23,42,0.08)',
            background: '#fff',
            fontSize: 14.5,
            color: '#1f2937',
          },
          '& .u-muted': { color: '#1f2937', fontWeight: 400 },
        }}
      />
    </Box>
  );
};

export default DevelopmentRentalAssumptionsReadOnly;

